import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { SERVICES, CITIES, BUSINESS } from "@/data/config";

// On Namecheap: form POSTs to /sendmail.php, which replies with JSON.
// On success we route to /thanks (a real URL you can drop conversion pixels on).
// On this preview platform /sendmail.php does not exist (no PHP host here), so
// submitting here will surface the same error state a real failed send would —
// that is expected in preview and is not a bug.

const initial = {
  name: "",
  email: "",
  phone: "",
  city: "",
  service: "",
  message: "",
  hp: "", // honeypot
};

export default function QuoteForm({ preselectedService, preselectedCity, compact = false }) {
  const navigate = useNavigate();
  const [values, setValues] = useState({
    ...initial,
    service: preselectedService || "",
    city: preselectedCity || "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!values.name.trim()) return "Please enter your name.";
    if (!values.email.trim() && !values.phone.trim())
      return "Please enter an email or a phone number so we can reach you.";
    if (values.email && !/^\S+@\S+\.\S+$/.test(values.email))
      return "Please enter a valid email address.";
    return "";
  };

  const failMessage = `Something went wrong sending your request. Please call us directly at ${BUSINESS.phone} or email ${BUSINESS.email}.`;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    if (values.hp) return; // silent honeypot

    setStatus("submitting");
    try {
      const body = new FormData();
      Object.entries(values).forEach(([k, val]) => body.append(k, val));
      const res = await fetch("/sendmail.php", {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.ok) {
        navigate("/thanks");
        return;
      }
      setStatus("idle");
      setError(data?.error ? `${data.error}. ${failMessage}` : failMessage);
    } catch {
      setStatus("idle");
      setError(failMessage);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      action="/sendmail.php"
      method="POST"
      noValidate
      data-testid="quote-form"
      className={`border border-white/10 bg-ink-900 rounded-sm ${compact ? "p-6 md:p-8" : "p-6 md:p-10"} space-y-5`}
    >
      {!compact && (
        <div className="mb-2">
          <p className="overline mb-3">Request a free quote</p>
          <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tightest">
            Tell us about your roof. We reply within one business day.
          </h3>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name" name="name" value={values.name} onChange={handleChange} required testid="input-name" />
        <Field label="Phone" name="phone" type="tel" value={values.phone} onChange={handleChange} testid="input-phone" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Email" name="email" type="email" value={values.email} onChange={handleChange} testid="input-email" />
        <Select label="City" name="city" value={values.city} onChange={handleChange} testid="input-city">
          <option value="">Select your city</option>
          {CITIES.map((c) => (
            <option key={c.slug} value={c.name}>{c.name}</option>
          ))}
          <option value="Other">Other Inland Northwest</option>
        </Select>
      </div>
      <Select label="Service needed" name="service" value={values.service} onChange={handleChange} testid="input-service">
        <option value="">Select a service</option>
        {SERVICES.map((s) => (
          <option key={s.slug} value={s.name}>{s.name}</option>
        ))}
        <option value="Not sure yet">Not sure yet</option>
      </Select>
      <TextArea
        label="Anything we should know?"
        name="message"
        value={values.message}
        onChange={handleChange}
        testid="input-message"
        placeholder="Roof type, number of stories, ideal timing, anything else."
      />

      {/* Honeypot */}
      <input
        type="text"
        name="hp"
        value={values.hp}
        onChange={handleChange}
        className="absolute -left-[9999px]"
        tabIndex={-1}
        autoComplete="off"
      />

      {error && (
        <p className="text-sm text-red-400 font-heading" data-testid="quote-form-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        data-testid="quote-submit"
        className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-copper-500 hover:bg-copper-600 disabled:opacity-70 text-ink-950 hover:text-white font-heading font-semibold text-base px-8 py-4 rounded-sm transition-colors duration-200"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending
          </>
        ) : (
          "Send Request"
        )}
      </button>

      <p className="text-xs text-bone-400 font-heading pt-2">
        Your info is used to reply to your quote. Never shared. Never sold.
      </p>
    </form>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-bone-400 font-heading mb-2">
        {label}
      </span>
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-ink-800 border border-white/10 focus:border-copper-500 rounded-sm px-4 py-3 text-bone-50 placeholder:text-bone-400 outline-none transition-colors duration-200"
      />
    </label>
  );
}
function Select({ label, name, value, onChange, children, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-bone-400 font-heading mb-2">
        {label}
      </span>
      <select
        data-testid={testid}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full bg-ink-800 border border-white/10 focus:border-copper-500 rounded-sm px-4 py-3 text-bone-50 outline-none transition-colors duration-200"
      >
        {children}
      </select>
    </label>
  );
}
function TextArea({ label, name, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-bone-400 font-heading mb-2">
        {label}
      </span>
      <textarea
        data-testid={testid}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={4}
        className="w-full bg-ink-800 border border-white/10 focus:border-copper-500 rounded-sm px-4 py-3 text-bone-50 placeholder:text-bone-400 outline-none transition-colors duration-200 resize-none"
      />
    </label>
  );
}
